
import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  ThumbsUp, 
  ThumbsDown, 
  HelpCircle,
  ArrowUpDown
} from 'lucide-react';
import { KnowledgeEffectiveness } from '../types';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { calculateContentQualityScore } from '@/services/ai-knowledge-processing-service';

interface KnowledgeEffectivenessTableProps {
  data: KnowledgeEffectiveness[];
  isLoading: boolean;
  limit?: number;
  simplified?: boolean;
}

export const KnowledgeEffectivenessTable: React.FC<KnowledgeEffectivenessTableProps> = ({ 
  data, 
  isLoading, 
  limit = 10,
  simplified = false
}) => {
  const [sortField, setSortField] = React.useState<string>('relevanceScore');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  
  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Apply sorting
  const sortedData = [...data].sort((a: any, b: any) => {
    if (sortDirection === 'asc') {
      return a[sortField] > b[sortField] ? 1 : -1;
    } else {
      return a[sortField] < b[sortField] ? 1 : -1;
    }
  });

  // Apply limit
  const displayData = sortedData.slice(0, limit);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full" />
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // If no data
  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No knowledge effectiveness data available yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[400px]">Document Title</TableHead>
            <TableHead>
              <Button variant="ghost" onClick={() => toggleSort('relevanceScore')} className="flex items-center">
                Relevance
                <ArrowUpDown size={14} className="ml-1" />
              </Button>
            </TableHead>
            <TableHead className="text-right">Usage Count</TableHead>
            <TableHead className="text-right">Quality Score</TableHead>
            {!simplified && (
              <TableHead className="text-right">Feedback</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayData.map((item) => (
            <TableRow key={item.documentId} className="hover:bg-muted/20">
              <TableCell className="font-medium">{item.title || 'Unknown Document'}</TableCell>
              <TableCell>
                <div className="flex items-center">
                  <div className="bg-primary h-2 rounded" style={{ 
                    width: `${Math.min(item.relevanceScore * 100, 100)}%`,
                    backgroundColor: getRelevanceColor(item.relevanceScore)
                  }} />
                  <span className="ml-2">{(item.relevanceScore * 100).toFixed(1)}%</span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                {item.usageCount || 0}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end">
                  <div className="bg-primary h-2 rounded" style={{ 
                    width: `${Math.min(item.qualityScore * 100, 100)}%`,
                    backgroundColor: getQualityColor(item.qualityScore)
                  }} />
                  <span className="ml-2">{((item.qualityScore || 0) * 100).toFixed(0)}%</span>
                </div>
              </TableCell>
              {!simplified && (
                <TableCell className="text-right">
                  {renderFeedbackIcon(item.feedbackScore)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// Helper function to get color based on relevance score
const getRelevanceColor = (score: number): string => {
  if (score >= 0.8) return '#0ac389'; // High relevance - green
  if (score >= 0.5) return '#64B5F6'; // Medium relevance - blue
  return '#F97316'; // Low relevance - orange
};

// Helper function to get color based on quality score
const getQualityColor = (score: number): string => {
  if (!score) return '#9CA3AF'; // Gray for unknown
  if (score >= 0.85) return '#0ac389'; // High quality - green
  if (score >= 0.7) return '#64B5F6'; // Medium quality - blue
  if (score >= 0.5) return '#F97316'; // Low quality - orange
  return '#ea384c'; // Poor quality - red
};

// Helper function to render feedback icon
const renderFeedbackIcon = (feedbackScore: number | null) => {
  if (feedbackScore === 1) {
    return <ThumbsUp className="ml-auto" size={16} color="#0ac389" />;
  } else if (feedbackScore === -1) {
    return <ThumbsDown className="ml-auto" size={16} color="#ea384c" />;
  } else {
    return <HelpCircle className="ml-auto" size={16} color="#9CA3AF" />;
  }
};
